import React, { useState } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, Checkbox,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import TextField from 'components/CustomFields';
import CustomButton from 'components/CustomButton';
import CustomDatePicker from 'components/CustomDatePicker';
import ConfirmationDialog from 'dialogs/ConfirmationDialog';
import useResumeStore from './store';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(4, 6),
    overflowY: 'auto',
    flex: 1,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.neutral['clr-300'],
      borderRadius: '3px',
    },
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    width: 'fit-content',
    padding: theme.spacing(2, 0),
    '&:hover': {
      opacity: 0.8,
    },
  },
  accordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.neutral['clr-300']}`,
    borderRadius: `${theme.spacing(1)} !important`,
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 !important',
    },
  },
  accordionSummary: {
    minHeight: '56px !important',
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '0 !important',
    },
  },
  summaryLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(0, 4, 4, 4),
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
    paddingTop: theme.spacing(4),
  },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  dateRow: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  summarySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  entryFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
  },
  summaryActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  enhanceButton: {
    background: 'linear-gradient(90deg, #1961BF 0%, #0D47A1 100%)',
    color: theme.palette.shades['clr-white-900'],
    borderRadius: '20px',
    padding: theme.spacing(2, 5),
    cursor: 'not-allowed',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    transition: 'opacity 0.3s ease',
    width: 'fit-content',
    opacity: 0.6,
    position: 'relative',
  },
  comingSoonTag: {
    background: theme.palette.warning?.main || '#FF9800',
    color: theme.palette.shades?.['clr-white-900'] || '#fff',
    borderRadius: '4px',
    padding: theme.spacing(0.25, 1),
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '14px',
    whiteSpace: 'nowrap',
  },
  addSummaryButton: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary['clr-50'],
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    gap: theme.spacing(2),
  },
}));

const WorkExperienceEntry = ({ index, data, expanded, onToggle, onUpdate, onDelete }) => {
  const classes = useStyles();

  const handleChange = (field) => (e) => {
    onUpdate(index, { [field]: e.target.value });
  };

  const handleDateChange = (field) => (value) => {
    onUpdate(index, { [field]: value });
  };

  const handleSummaryChange = (pointIndex) => (e) => {
    const updatedPoints = [...data.summaryPoints];
    updatedPoints[pointIndex] = e.target.value;
    onUpdate(index, { summaryPoints: updatedPoints });
  };

  const handleAddSummaryPoint = () => {
    onUpdate(index, { summaryPoints: [...data.summaryPoints, ''] });
  };

  const handleRemoveSummaryPoint = (pointIndex) => {
    const updatedPoints = data.summaryPoints.filter((_, i) => i !== pointIndex);
    onUpdate(index, { summaryPoints: updatedPoints });
  };

  const title = data.title || data.jobRole
    ? `${data.title || 'Untitled'}${data.jobRole ? ` — ${data.jobRole}` : ''}`
    : `Work Experience ${index + 1}`;

  const subtitle = data.startDate || data.endDate
    ? `${data.startDate || ''}${data.startDate && data.endDate ? ' - ' : ''}${data.endDate || ''}`
    : '';

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={() => onToggle(index)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.accordionSummary}
      >
        <Box className={classes.summaryLeft}>
          <Typography variant='body01-bold' color='neutral.clr-900'>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant='body02-medium' color='neutral.clr-500'>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          size='small'
          onClick={(e) => { e.stopPropagation(); onDelete(index); }}
          sx={{ mr: 1 }}
        >
          <DeleteOutlineIcon fontSize='small' color='error' />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Box className={classes.fieldRow}>
          <Box flex={1}>
            <TextField
              label='Title'
              placeholder='Title of the project'
              value={data.title}
              onChange={handleChange('title')}
              required
            />
          </Box>
          <Box flex={1}>
            <TextField
              label='Job Role'
              placeholder='Role in project'
              value={data.jobRole}
              onChange={handleChange('jobRole')}
              required
            />
          </Box>
        </Box>

        <Box className={classes.fieldRow}>
          <Box flex={1}>
            <TextField
              label='Project Link'
              placeholder='https://your-project-url.com'
              value={data.projectLink || ''}
              onChange={handleChange('projectLink')}
            />
          </Box>
        </Box>

        <Box className={classes.dateRow}>
          <Box flex={1}>
            <CustomDatePicker
              id={`work-start-date-${index}`}
              type='month-year'
              label='Start Date'
              placeholder='Select date'
              initialDate={data.startDate ? new Date(data.startDate) : null}
              onChange={handleDateChange('startDate')}
            />
          </Box>
          <Box flex={1}>
            <CustomDatePicker
              id={`work-end-date-${index}`}
              type='month-year'
              label='End Date'
              placeholder='Select date'
              initialDate={data.endDate ? new Date(data.endDate) : null}
              onChange={handleDateChange('endDate')}
              disabled={!data.startDate || data.currentlyOngoing}
              minDate={data.startDate ? new Date(data.startDate) : null}
            />
            <Box display='flex' alignItems='center' mt={1}>
              <Checkbox
                checked={data.currentlyOngoing || false}
                onChange={(e) => {
                  onUpdate(index, {
                    currentlyOngoing: e.target.checked,
                    endDate: e.target.checked ? '' : data.endDate,
                  });
                }}
                color='primary'
                size='small'
                sx={{ padding: 0, mr: 1 }}
              />
              <Typography variant='body02-medium' color='neutral.clr-700'>
                Currently Ongoing
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={classes.summarySection}>
          <Typography variant='h6-semiBold' color='neutral.clr-900'>
            Summary
          </Typography>
          {data.summaryPoints.map((point, pointIndex) => (
            <Box key={pointIndex} display='flex' gap={1} alignItems='flex-start'>
              <Box flex={1}>
                <TextField
                  placeholder='Developed a Case master using JS react ...'
                  value={point}
                  onChange={handleSummaryChange(pointIndex)}
                />
              </Box>
              <IconButton
                size='small'
                onClick={() => handleRemoveSummaryPoint(pointIndex)}
                sx={{ mt: '4px' }}
              >
                <DeleteOutlineIcon fontSize='small' color='error' />
              </IconButton>
            </Box>
          ))}
          <Box className={classes.summaryActions}>
            <Box
              component='button'
              className={classes.enhanceButton}
              disabled
            >
              <Typography variant='body01-bold' color='shades.clr-white-900'>
                Enhance with AI
              </Typography>
              <Box component='span' className={classes.comingSoonTag}>
                Coming Soon
              </Box>
            </Box>
            <Box
              component='button'
              className={classes.addSummaryButton}
              onClick={handleAddSummaryPoint}
            >
              <AddIcon sx={{ fontSize: '20px', color: 'primary.main' }} />
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const WorkExperienceForm = ({ onNext }) => {
  const classes = useStyles();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const [
    workExperiences, addWorkExperience, updateWorkExperience, removeWorkExperience,
  ] = useResumeStore((state) => [
    state.workExperiences, state.addWorkExperience,
    state.updateWorkExperience, state.removeWorkExperience,
  ]);

  const handleAdd = () => {
    addWorkExperience();
    setExpandedIndex(workExperiences.length);
  };

  const handleToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleDelete = (index) => {
    setDeleteIndex(index);
  };

  const handleConfirmDelete = () => {
    removeWorkExperience(deleteIndex);
    if (expandedIndex === deleteIndex) {
      setExpandedIndex(null);
    } else if (expandedIndex > deleteIndex) {
      setExpandedIndex(expandedIndex - 1);
    }
    setDeleteIndex(null);
  };

  const isNextDisabled = workExperiences.length === 0 ||
    workExperiences.some((exp) =>
      !exp.title?.trim() ||
      !exp.jobRole?.trim()
    );

  return (
    <Box className={classes.container}>
      <Box className={classes.content}>
        {workExperiences.length === 0 && (
          <Box className={classes.emptyState}>
            <Typography variant='body01-medium' color='neutral.clr-400'>
              No work experience added yet. Click the button below to add one.
            </Typography>
          </Box>
        )}

        {workExperiences.map((entry, index) => (
          <WorkExperienceEntry
            key={index}
            index={index}
            data={entry}
            expanded={expandedIndex === index}
            onToggle={handleToggle}
            onUpdate={updateWorkExperience}
            onDelete={handleDelete}
          />
        ))}

        <Box className={classes.addButton} onClick={handleAdd}>
          <AddIcon sx={{ fontSize: '20px', color: 'primary.main' }} />
          <Typography variant='body01-bold' color='primary.main'>
            Add Work Experience
          </Typography>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Typography variant='body02-bold' color='warning.main'>
          Note: Your data will be lost if you leave this page.
        </Typography>
        <CustomButton
          size='medium'
          onClick={onNext}
          disabled={isNextDisabled}
          sx={{ width: 'fit-content' }}
        >
          Next: Education
        </CustomButton>
      </Box>

      <ConfirmationDialog
        open={deleteIndex !== null}
        title='Delete Work Experience'
        message='Are you sure you want to delete this work experience?'
        primaryAction='Delete'
        secondaryAction='Cancel'
        onPrimaryAction={handleConfirmDelete}
        onSecondaryAction={() => setDeleteIndex(null)}
      />
    </Box>
  );
};

export default WorkExperienceForm;
